import { Reducer, useEffect, useReducer, useRef, useCallback } from 'react';
import { Option, none, some } from 'fp-ts/Option';
import { Either, fold as foldEither } from 'fp-ts/Either';

import { ErrorData } from 'domain/types';

export enum ACTION_TYPE {
    SET_LOADING = 'SET_LOADING',
    SET_DATA = 'SET_DATA',
    SET_ERROR = 'SET_ERROR',
    RESET = 'RESET',
}

type SetLoading = {
    type: ACTION_TYPE.SET_LOADING;
    payload: boolean;
};

type SetData<T> = {
    type: ACTION_TYPE.SET_DATA;
    payload: Option<T>;
};

type SetError = {
    type: ACTION_TYPE.SET_ERROR;
    payload: Option<ErrorData>;
};

type Reset = {
    type: ACTION_TYPE.RESET;
};

export type UseCaseState = {
    isLoading: boolean;
    error: Option<ErrorData>;
};

export type UseCaseDispatch = {
    setLoading: (loading: boolean) => void;
    setError: (error: ErrorData) => void;
};

export type DefaultUseCaseState<T> = UseCaseState & {
    data: Option<T>;
};

type UseCaseAction<T> = SetLoading | SetData<T> | SetError | Reset;
// eslint-disable-next-line consistent-return
export const useCaseReducer = <T>(state: DefaultUseCaseState<T>, action: UseCaseAction<T>): DefaultUseCaseState<T> => {
    // Todo why need payload?
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { type, payload } = action;
    // eslint-disable-next-line default-case
    switch (type) {
        case ACTION_TYPE.SET_LOADING: {
            return {
                ...state,
                isLoading: payload as boolean,
                data: none,
                error: none,
            };
        }

        case ACTION_TYPE.SET_DATA: {
            return {
                ...state,
                isLoading: false,
                data: payload as Option<T>,
                error: none,
            };
        }

        case ACTION_TYPE.SET_ERROR: {
            return {
                ...state,
                isLoading: false,
                data: none,
                error: payload as Option<ErrorData>,
            };
        }

        case ACTION_TYPE.RESET: {
            return {
                isLoading: false,
                data: none,
                error: none,
            };
        }
    }
};

export type DefaultUseCaseDispatch<T> = UseCaseDispatch & {
    setData: (data: T) => void;
    resetLoading: () => void;
    resetData: () => void;
    resetError: () => void;
    reset: () => void;
};

export const initialStateUseCase = <T>(isLoading: boolean): DefaultUseCaseState<T> => ({
    isLoading,
    data: none,
    error: none,
});

// TODO Types, might be necessary AbstractUseCase and AbstractCriteria
type Criteria = any;
export type CriteriaCallback = (criteria?: Criteria) => void;

export const useCase = <T>(
    useCaseFunction: (criteria?: Criteria) => Promise<Either<ErrorData, T>>,
    immediate = false,
    criteria?: Criteria
): (DefaultUseCaseState<T> & DefaultUseCaseDispatch<T>) & { execute: CriteriaCallback } => {
    const [state, dispatch] = useReducer<Reducer<DefaultUseCaseState<T>, UseCaseAction<T>>>(useCaseReducer, initialStateUseCase(immediate));
    const setLoading = (loading: boolean) => dispatch({ type: ACTION_TYPE.SET_LOADING, payload: loading });
    const setError = (error: ErrorData) => dispatch({ type: ACTION_TYPE.SET_ERROR, payload: some(error) });
    const setData = (data: T) => dispatch({ type: ACTION_TYPE.SET_DATA, payload: some(data) });
    const resetLoading = () => dispatch({ type: ACTION_TYPE.SET_LOADING, payload: false });
    const resetError = () => dispatch({ type: ACTION_TYPE.SET_ERROR, payload: none });
    const resetData = () => dispatch({ type: ACTION_TYPE.SET_DATA, payload: none });
    const reset = () => dispatch({ type: ACTION_TYPE.RESET });

    // useCallback ensures the below useEffect is not called
    // on every render, but only if domainUseCase changes.
    const execute = useCallback(
        (_criteria?: Criteria) => {
            setLoading(true);
            useCaseFunction(_criteria).then(foldEither(setError, setData));
        },
        [useCaseFunction]
    );

    const criteriaRef = useRef(criteria);

    useEffect(() => {
        if (immediate) {
            if (criteria) {
                execute(criteria);
            } else {
                execute();
            }
        }
    }, [criteriaRef.current]);

    return {
        isLoading: state.isLoading,
        data: state.data,
        error: state.error,
        setLoading,
        setError,
        setData,
        resetLoading,
        resetError,
        resetData,
        reset,
        execute,
    };
};
